/**
 * MaterialDetailsSheet — in-portal material BOM viewer for installer/sub roles.
 *
 * Replaces the legacy `/job/:id` link out of AlignmentBlockCard /
 * ChangeOrderHaltCard, which routed installers into the operator JobDetail
 * (P12.2 hard-rule violation).
 *
 * Read-only. Reads the canonical BOM via getJobBom(). No prices, no costs.
 */

import { useState } from "react";
import { Package, Box } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";
import { Badge } from "@/components/ui/badge";
import { useAppData } from "@/data/AppDataContext";
import { getJobBom, getJobBomSourceLabel } from "@/data/jobBomSelector";

export function MaterialDetailsSheet({ jobId }: { jobId: string }) {
  const [open, setOpen] = useState(false);
  const { jobs } = useAppData();
  const job = jobs.find((j) => j.id === jobId);

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetTrigger asChild>
        <Button variant="outline" size="sm">
          <Package className="h-3.5 w-3.5 mr-1.5" />
          View material details
        </Button>
      </SheetTrigger>
      <SheetContent
        side="bottom"
        className="max-h-[85vh] overflow-y-auto"
        data-testid="material-details-sheet"
      >
        <SheetHeader>
          <SheetTitle className="text-base">Material kit</SheetTitle>
          <SheetDescription className="text-xs">
            What this job is supposed to ship with. Read-only.
          </SheetDescription>
        </SheetHeader>

        {!job ? (
          <p className="text-xs text-muted-foreground py-6">
            Job not found.
          </p>
        ) : (
          <MaterialList jobId={jobId} />
        )}
      </SheetContent>
    </Sheet>
  );
}

function MaterialList({ jobId }: { jobId: string }) {
  const { jobs } = useAppData();
  const job = jobs.find((j) => j.id === jobId)!;
  const bom = getJobBom(job);

  if (bom.items.length === 0) {
    return (
      <div className="py-6 text-center text-xs text-muted-foreground">
        <Box className="h-6 w-6 mx-auto mb-2 opacity-50" />
        No material kit on file yet. Contact your PM.
      </div>
    );
  }

  return (
    <div className="mt-3 space-y-2">
      <div className="flex items-center justify-between">
        <p className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
          {bom.items.length} item{bom.items.length === 1 ? "" : "s"}
        </p>
        <Badge variant="outline" className="text-[10px]">
          {getJobBomSourceLabel(bom.source)}
        </Badge>
      </div>
      <ul className="rounded-lg border border-border divide-y divide-border">
        {bom.items.map((item) => (
          <li
            key={`${item.sku}-${item.name}`}
            className="flex items-center justify-between gap-3 px-3 py-2.5"
          >
            <div className="min-w-0 flex-1">
              <p className="text-xs font-medium truncate">{item.name}</p>
              <p className="text-[10px] font-mono text-muted-foreground">
                {item.sku}
              </p>
            </div>
            <span className="text-xs font-semibold tabular-nums shrink-0">
              {item.quantity}
              {item.unit ? ` ${item.unit}` : ""}
            </span>
          </li>
        ))}
      </ul>
      <p className="text-[10px] text-muted-foreground pt-1">
        Quantities and prep status are managed by your PM. No override is
        available from this surface.
      </p>
    </div>
  );
}
